import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { connect } from "react-redux";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import { request } from "../services/networking/request";
import { parseDate } from "../utils/helpers/date";
import NavBar from "../components/layout/NavBar";
import ListingForm from "../components/ListingForm";
import Modal from "../components/Modal";
import OuterContainer from "../components/layout/OuterContainer";
import SafetyTipsPanel from "../components/safety/SafetyTipsPanel";

//================================================================//
//  🎨 DESIGN SYSTEM & PAGE STYLES
//================================================================//

const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
    error: "#dc2626",
    errorLight: "#fef2f2",
  },
  shadows: {
    sm: "0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 4px 12px rgba(15, 23, 42, 0.06)",
    lg: "0 6px 16px rgba(15, 23, 42, 0.06)",
  },
  borderRadius: "12px",
};

const PageWrapper = styled.div`
  background: ${theme.colors.background};
  min-height: 100vh;
  // padding-top: 64px;
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 24px;

  @media (max-width: 768px) {
    padding: 24px 0;
    margin: 0;
  }
`;

const ContentPane = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius};
  border: 1px solid ${theme.colors.border};
  box-shadow: none;
  padding: 40px;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 24px 20px;
    border-radius: 12px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.textHeading};
  margin: 0 0 8px;
  padding: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 6px;
  }
`;

const HeaderSubtitle = styled.p`
  color: ${theme.colors.textMuted};
  font-size: 1rem;
  margin: 0 0 32px;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 24px;
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  background: #fff7f7;
  border: 1px solid #fecaca;
  padding: 12px 16px;
  margin: 0 0 24px;
  text-align: center;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  box-shadow: none;
`;

const InfoBox = styled.div`
  background: #fffaf0;
  border: 1px solid #fde68a;
  border-radius: 10px;
  padding: 12px 16px;
  margin: 0 0 24px;
  font-size: 0.9rem;
  color: #92400e;
  line-height: 1.5;
  box-shadow: none;

  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 0.85rem;
  }
`;

const InfoBoxTitle = styled.div`
  font-weight: 600;
  color: #78350f;
  margin-bottom: 6px;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const BrowseTasksLink = styled.a`
  color: #1d4ed8;
  text-decoration: none;
  font-weight: 700;
  background: ${theme.colors.primaryLight};
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.2s ease, color 0.2s ease;
  
  &:hover {
    text-decoration: none;
    background: #dbeafe;
    color: #1e40af;
  }
`;

//================================================================//
//  💀 SKELETON LOADER COMPONENT
//================================================================//

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const SkeletonBase = styled.div`
  background: #f1f5f9;
  background: linear-gradient(110deg, #f1f5f9 8%, #ffffff 18%, #f1f5f9 33%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: 12px;
`;

const SkeletonSection = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius};
  padding: 28px;
  box-shadow: none;
  
  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

const SkeletonForm = styled.div`
  display: grid;
  gap: 32px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    gap: 24px;
    margin-top: 20px;
  }
`;

const SafetyPanelSkeleton = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  box-shadow: none;
  overflow: hidden;
  margin-bottom: 20px;
`;

const SkeletonLoader = () => (
  <>
    <SkeletonBase
      style={{ height: "28px", width: "250px", marginBottom: "8px" }}
    />
    <SkeletonBase
      style={{ height: "18px", width: "400px", marginBottom: "32px" }}
    />

    {/* Safety Tips Panel Skeleton */}
    <SafetyPanelSkeleton>
      <div style={{ 
        padding: "16px 20px", 
        background: "#f8fafc",
        borderBottom: "1px solid #e2e8f0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <SkeletonBase style={{ height: "20px", width: "20px", borderRadius: "4px" }} />
          <SkeletonBase style={{ height: "16px", width: "120px" }} />
        </div>
        <SkeletonBase style={{ height: "20px", width: "20px", borderRadius: "4px" }} />
      </div>
    </SafetyPanelSkeleton>

    <SkeletonForm>
      <SkeletonSection>
      <SkeletonBase
        style={{ height: "16px", width: "120px", marginBottom: "10px" }}
      />
      <SkeletonBase style={{ height: "46px", width: "100%" }} />
      <SkeletonBase
        style={{
          height: "16px",
          width: "140px",
          marginTop: "14px",
          marginBottom: "10px",
        }}
      />
      <SkeletonBase style={{ height: "120px", width: "100%" }} />
    </SkeletonSection>

    <SkeletonSection>
      <SkeletonBase
        style={{ height: "16px", width: "100px", marginBottom: "20px" }}
      />
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}
      >
        <SkeletonBase style={{ height: "60px" }} />
        <SkeletonBase style={{ height: "60px" }} />
      </div>
    </SkeletonSection>

    <SkeletonSection>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px" }}
      >
        <div>
          <SkeletonBase
            style={{ height: "16px", width: "80px", marginBottom: "10px" }}
          />
          <SkeletonBase style={{ height: "46px", width: "100%" }} />
        </div>
        <div>
          <SkeletonBase
            style={{ height: "16px", width: "100px", marginBottom: "10px" }}
          />
          <SkeletonBase style={{ height: "46px", width: "100%" }} />
        </div>
      </div>
      <SkeletonBase
        style={{
          height: "16px",
          width: "180px",
          marginTop: "16px",
          marginBottom: "10px",
        }}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "10px",
        }}
      >
        <SkeletonBase style={{ height: "40px", borderRadius: "20px" }} />
        <SkeletonBase style={{ height: "40px", borderRadius: "20px" }} />
        <SkeletonBase style={{ height: "40px", borderRadius: "20px" }} />
      </div>
    </SkeletonSection>

    <SkeletonSection>
      <SkeletonBase
        style={{ height: "16px", width: "120px", marginBottom: "20px" }}
      />
      <SkeletonBase style={{ height: "120px", width: "100%" }} />
    </SkeletonSection>

      <SkeletonBase
        style={{
          height: "48px",
          width: "140px",
          marginTop: "24px",
          borderRadius: "12px",
        }}
      />
    </SkeletonForm>
  </>
);

//================================================================//
//  🚀 PAGE COMPONENT
//================================================================//

const PostTask = (props) => {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const location = useLocation();
  const isEditMode = !!taskId;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    zipcode: "",
    budget: "",
    budget_type: "total",
    taskType: "in-person",
    dueDate: "",
    selectedTimeWindows: [],
    category_id: "",
  });
  const [timeWindows, setTimeWindows] = useState(null);
  const [categories, setCategories] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [uploadMeta, setUploadMeta] = useState({
    image_keys: [],
    image_urls: [],
  });
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [twResponse, categoriesResponse] = await Promise.all([
          request("POST", API_CONSTANT_MAP.fetch_task_time_windows),
          request("POST", API_CONSTANT_MAP.fetch_categories)
        ]);
        
        if (!twResponse.data.success)
          throw new Error("Failed to load time windows.");
        setTimeWindows(twResponse.data.time_windows);
        
        if (!categoriesResponse.data.success)
          throw new Error("Failed to load categories.");
        setCategories(categoriesResponse.data.categories);

        if (isEditMode) {
          const taskResponse = await request(
            "POST",
            API_CONSTANT_MAP.fetch_task_by_id,
            { task_id: taskId }
          );
          if (!taskResponse.data.success)
            throw new Error("Failed to load task details.");

          const task = taskResponse.data.task;
          setFormData({
            title: task.title || "",
            description: task.description || "",
            zipcode: task.zipcode || "",
            budget: task.budget?.toString() || "",
            budget_type: task.budget_type || "total",
            taskType: task.task_type || "in-person",
            dueDate: task.due_date
              ? parseDate(task.due_date)?.toISOString().split("T")[0] || ""
              : "",
            selectedTimeWindows: Array.isArray(task.time_windows)
              ? task.time_windows.map((tw) =>
                  typeof tw === "number" ? tw : tw.id
                )
              : [],
            category_id: task.category_id || "",
          });
          if (Array.isArray(task.images)) {
            setUploadMeta({
              image_keys: task.images.map((img) => img.s3_key),
              image_urls: task.images.map((img) => img.url),
            });
          }
        }
      } catch (err) {
        setErrorMessage(err.message || "An error occurred fetching data.");
        setErrorModal(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [isEditMode, taskId]);

  // Handle URL parameter for pre-filling task description
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const description = urlParams.get('description');
    if (description && !isEditMode) {
      setFormData(prev => ({ ...prev, title: description }));
    }
  }, [location.search, isEditMode]);

  const handleFormFieldChange = useCallback((e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    // Clear validation error when user starts typing
    if (validationErrors[id]) {
      setValidationErrors((prev) => ({ ...prev, [id]: false }));
    }
  }, [validationErrors]);

  const handleBudgetChange = useCallback((e) => {
    const val = e.target.value;
    if (/^\d*\.?\d{0,2}$/.test(val)) {
      setFormData((prev) => ({ ...prev, budget: val }));
      // Clear validation error when user starts typing
      if (validationErrors.budget) {
        setValidationErrors((prev) => ({ ...prev, budget: false }));
      }
    }
  }, [validationErrors]);

  const handleTaskTypeChange = useCallback((type) => {
    setFormData((prev) => ({ ...prev, taskType: type }));
    // Clear zipcode validation error when switching to remote
    if (type === "remote" && validationErrors.zipcode) {
      setValidationErrors((prev) => ({ ...prev, zipcode: false }));
    }
  }, [validationErrors]);

  const handleTimeWindowToggle = useCallback((id) => {
    setFormData((prev) => ({
      ...prev,
      selectedTimeWindows: prev.selectedTimeWindows.includes(id)
        ? prev.selectedTimeWindows.filter((w) => w !== id)
        : [...prev.selectedTimeWindows, id],
    }));
  }, []);

  const onImagesChange = useCallback((meta) => {
    setUploadMeta(meta);
  }, []);

  const onImageDelete = useCallback((key) => {
    setImagesToDelete((prev) => [...prev, key]);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const requiredFields = {
      title: "Title",
      description: "Description",
      budget: "Budget",
      category_id: "Category",
      ...(formData.taskType === "in-person" && { zipcode: "ZIP Code" }),
    };

    const errors = {};
    for (const field in requiredFields) {
      const value = formData[field];
      if (!value || (typeof value === 'string' && value.trim() === "")) {
        errors[field] = true;
      }
    }
    
    setValidationErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      setErrorMessage(`Please fill in all required fields.`);
      setErrorModal(true);
      setIsLoading(false);
      return;
    }
    if (formData.selectedTimeWindows.length === 0) {
      setErrorMessage("Please select at least one time window.");
      setErrorModal(true);
      setIsLoading(false);
      return;
    }

    try {
      const endpoint = isEditMode
        ? API_CONSTANT_MAP.update_task
        : API_CONSTANT_MAP.create_task;
      const payload = {
        ...formData,
        task_id: taskId,
        image_keys: uploadMeta.image_keys,
        image_urls: uploadMeta.image_urls,
        images_to_delete: imagesToDelete,
      };
      const response = await request("POST", endpoint, payload);

      if (response.data.success) {
        navigate(isEditMode ? `/tasks/${taskId}` : "/dashboard");
      } else {
        throw new Error(response.data.message || "Failed to save the task.");
      }
    } catch (err) {
      setErrorMessage(err.message);
      setErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <NavBar user={props.state.user} />
      <PageWrapper>
        <OuterContainer>
          <ContentContainer>
            {isLoading ? (
              <ContentPane>
                <SkeletonLoader />
              </ContentPane>
            ) : (
              <ContentPane>
                <HeaderTitle>
                  {isEditMode ? "Edit Your Task" : "Post a New Task"}
                </HeaderTitle>
                <HeaderSubtitle>
                  {isEditMode
                    ? "Update your task details and requirements below."
                    : "Tell us what you need help with and find the perfect person for the job."}
                </HeaderSubtitle>

                {/* Info box for service providers (post mode) or edit tips (edit mode) */}
                {!isEditMode ? (
                  <InfoBox>
                    <InfoBoxTitle>Are you a service provider looking for work?</InfoBoxTitle>
                    This page is for customers who need help with tasks. If you're a handyman, contractor, or service provider looking for jobs, you should <BrowseTasksLink href="/tasks">browse available tasks</BrowseTasksLink> instead and send offers to customers.
                  </InfoBox>
                ) : (
                  <InfoBox>
                    <InfoBoxTitle>Editing your task</InfoBoxTitle>
                    Update your task details below. Changes will be visible to service providers who have already made offers on this task.
                  </InfoBox>
                )}

                {/* Safety Tips Panel */}
                {/* <SafetyTipsPanel
                  context="posting"
                  showAlerts={true}
                  defaultExpanded={false}
                /> */}

                {timeWindows && categories ? (
                  <ListingForm
                    formData={formData}
                    timeWindows={timeWindows}
                    categories={categories}
                    handleSubmit={handleSubmit}
                    isEditMode={isEditMode}
                    uploadMeta={uploadMeta}
                    handleFormFieldChange={handleFormFieldChange}
                    handleTimeWindowToggle={handleTimeWindowToggle}
                    handleTaskTypeChange={handleTaskTypeChange}
                    handleBudgetChange={handleBudgetChange}
                    onImagesChange={onImagesChange}
                    onImageDelete={onImageDelete}
                    validationErrors={validationErrors}
                  />
                ) : (
                  <ErrorMessage>
                    Could not load form data. Please try again later.
                  </ErrorMessage>
                )}
              </ContentPane>
            )}
          </ContentContainer>
        </OuterContainer>
      </PageWrapper>
      <Modal isOpen={errorModal} onClose={() => setErrorModal(false)}>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({ state });
export default connect(mapStateToProps)(PostTask);
